"use client";

import Image from "next/image";
import Link from "next/link";
import Icon from "./Icon";
import ThemeMenu from "./ThemeMenu";
import NotificationPanel from "./NotificationPanel";
import MobileNav from "./MobileNav";
import { OPEN_COMMAND_PALETTE_EVENT } from "./CommandPalette";
import { useAuth } from "./AuthProvider";
import { assetUrl, roleLabel } from "../lib/api";

export default function Topbar() {
  const { user } = useAuth();

  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || user?.email || "—";
  const initials =
    `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase() ||
    (user?.email?.[0]?.toUpperCase() ?? "");
  const avatar = assetUrl(user?.profileImage);

  const openPalette = () => window.dispatchEvent(new Event(OPEN_COMMAND_PALETTE_EVENT));

  return (
    <header className="sticky top-0 z-30 -my-2 py-2 flex items-center gap-3 sm:gap-4">
      <MobileNav />

      {/* Both forms open the command palette — the field never accepts input,
          so on small screens it collapses to the icon alone rather than
          eating the width the nav and profile need. */}
      <button
        type="button"
        aria-label="Search"
        onClick={openPalette}
        className="neu-btn sm:hidden grid place-items-center w-12 h-12 rounded-2xl text-text-muted shrink-0"
      >
        <Icon name="search" size={18} />
      </button>

      <label className="neu-inset hidden sm:flex items-center gap-2.5 px-4 h-12 rounded-2xl text-text-muted flex-1 max-w-md">
        <Icon name="search" size={18} />
        <input
          readOnly
          onFocus={(e) => {
            e.target.blur();
            openPalette();
          }}
          placeholder="Search deals, customers, managers…"
          className="bg-transparent outline-none text-sm text-text placeholder:text-text-faint w-full cursor-pointer"
        />
        <kbd className="text-[10px] text-text-faint border border-current/20 rounded px-1.5 py-0.5">
          ⌘K
        </kbd>
      </label>

      <div className="flex-1" />

      <ThemeMenu />
      <NotificationPanel />

      <Link
        href="/profile"
        aria-label="Profile and security"
        className="neu-btn flex items-center gap-3 pl-2 pr-4 h-12 rounded-2xl shrink-0"
      >
        <span className="grid place-items-center w-8 h-8 rounded-xl overflow-hidden shrink-0">
          {avatar ? (
            <Image src={avatar} alt="" width={32} height={32} className="w-8 h-8 object-cover" />
          ) : (
            <span
              className="grid place-items-center w-8 h-8 rounded-xl text-white text-xs font-semibold"
              style={{ background: "var(--accent)" }}
            >
              {initials}
            </span>
          )}
        </span>
        <span className="hidden sm:block text-left leading-tight">
          <span className="block text-[13px] font-semibold text-text">{name}</span>
          <span className="block text-[11px] text-text-faint">{roleLabel(user?.role)}</span>
        </span>
      </Link>
    </header>
  );
}
